import { Grid, IconButton, Menu, MenuItem, Chip } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';
import axios from 'axios';
import StyledDashboardLayout from '../style';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useModuleHasPermissions } from '../../hooks/useModulePermissions';

function TechStackList() {
   const MySwal = withReactContent(Swal);
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const [techStackData, setTechStackData] = useState([]);
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);

   const hasPermissions = useModuleHasPermissions('tech_stack');
   // For action menu
   const [anchorEl, setAnchorEl] = useState(null);
   const [selectedRowId, setSelectedRowId] = useState(null);

   // For controlled input
   const [searchInput, setSearchInput] = useState('');
   const debounceTimeout = useRef(null);

   // New state for additional search fields
   const [searchTechnology, setSearchTechnology] = useState('');
   const [searchType, setSearchType] = useState('');

   // Fetch Tech Stack Data
   const fetchData = async () => {
      setLoading(true);
      try {
         const token = getAuthToken();
         const res = await axios.get(`${API_URL}technology-stack/list-technology`, {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (res.data && res.data.response) {
            // Sort data in descending order by ID (newest first)
            const sortedData = res.data.response.sort((a, b) => b.id - a.id);
            setTechStackData(sortedData);
         } else {
            setTechStackData([]);
         }
      } catch (error) {
         console.error('Error fetching tech stack data:', error);
         setTechStackData([]);
         toast.error('Failed to fetch technology stack data');
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchData();
   }, []);

   // Action menu handlers
   const handleMenuOpen = (event, rowId) => {
      setAnchorEl(event.currentTarget);
      setSelectedRowId(rowId);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedRowId(null);
   };

   const handleEdit = (id) => {
      navigate(`/tech-stack/edit-techstack/${id}`);
      handleMenuClose();
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this Technology Stack?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
         const token = getAuthToken();
         const response = await axios.delete(`${API_URL}technology-stack/delete-techstack/${id}`, {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (response.status === 200) {
            await MySwal.fire('Deleted!', 'Technology Stack has been deleted.', 'success');
            await fetchData(); // Refresh data after deletion
         } else {
            await MySwal.fire('Error!', 'Failed to delete Technology Stack.', 'error');
         }
      } catch (error) {
         console.error('Error deleting Technology Stack:', error);
         const errorMessage = error?.response?.data?.message || 'Failed to delete Technology Stack';
         await MySwal.fire({
            title: 'Denied!',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
         });
      }

      handleMenuClose();
   };

   // Search handler for technology (debounced, controlled input)
   const handleSearchTechnologyChange = (e) => {
      const value = e.target.value;
      setSearchTechnology(value);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearchQuery(value);
         setPage(1);
      }, 400);
   };

   // Search handler for type (client-side filter)
   const handleSearchTypeChange = (e) => {
      setSearchType(e.target.value);
   };

   // Filter data client-side for technology and type search
   const filteredData = techStackData.filter((row) => {
      const technologyMatch = searchTechnology
         ? row.technology?.toLowerCase().includes(searchTechnology.trim().toLowerCase())
         : true;
      const typeMatch = searchType
         ? row.type?.toLowerCase().includes(searchType.trim().toLowerCase())
         : true;
      return technologyMatch && typeMatch;
   });

   // Pagination state and logic
   const totalRows = filteredData.length;
   const totalPages = Math.ceil(totalRows / rowsPerPage);

   // Clamp page to valid range if data changes
   useEffect(() => {
      if (page > totalPages && totalPages > 0) {
         setPage(totalPages);
      }
      if (page < 1) {
         setPage(1);
      }
   }, [page, totalPages]);

   // Paginate the filtered data
   const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

   // Get color for technology type
   const getTypeColor = (type) => {
      const normalized = (type || '').toLowerCase().replace(/[\s\/_\-\.]/g, '');
      switch (normalized) {
         case 'frontend':
            return '#2196F3';
         case 'backend':
            return '#4CAF50';
         case 'database':
            return '#FF9800';
         case 'framework':
            return '#9C27B0';
         case 'cloud':
            return '#00BCD4';
         case 'devops':
            return '#FF5722';
         case 'mobile':
            return '#E91E63';
         case 'cms':
            return '#795548';
         case 'uiux':
            return '#5C6BC0'; // UI/UX - Indigo
         case 'seo':
            return '#FFC107'; // SEO - Amber
         default:
            return '#607D8B';
      }
   };

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => (page - 1) * rowsPerPage + index + 1,
         width: '8%',
         grow: 0,
      },
      {
         name: (
            <span
               style={{
                  fontSize: '17px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
               }}
            >
               Technology
            </span>
         ),
         selector: (row) => row.technology || '-',
         sortable: true,
         wrap: true,
         minWidth: '36%',
         grow: 1,
         cell: (row) => (
            <span style={{ fontWeight: 500, color: '#333' }}>{row.technology || '-'}</span>
         ),
      },
      {
         name: 'Type',
         selector: (row) => row.type || '-',
         sortable: true,
         wrap: true,
         minWidth: '22%',
         grow: 0,
         cell: (row) => (
            <Chip
               label={row.type || '-'}
               size="small"
               sx={{
                  backgroundColor: getTypeColor(row.type),
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.75rem',
               }}
            />
         ),
      },
      {
         name: 'Status',
         selector: (row) => row.status,
         sortable: true,
         wrap: true,
         minWidth: '22%',
         grow: 0,
         cell: (row) => (
            <Chip
               label={row.status ? 'Active' : 'Inactive'}
               size="small"
               color={row.status ? 'success' : 'error'}
               variant="outlined"
            />
         ),
      },
      {
         name: 'Action',
         cell: (row) => (
            <>
               <IconButton
                  aria-label="more"
                  aria-controls={`action-menu-${row.id}`}
                  aria-haspopup="true"
                  onClick={(event) => handleMenuOpen(event, row.id)}
               >
                  <MoreVertIcon />
               </IconButton>
               <Menu
                  id={`action-menu-${row.id}`}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedRowId === row.id}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                     vertical: 'bottom',
                     horizontal: 'right',
                  }}
                  transformOrigin={{
                     vertical: 'top',
                     horizontal: 'right',
                  }}
               >
                  <MenuItem
                     onClick={() => handleEdit(row.id)}
                     disabled={!hasPermissions?.canUpdate}
                  >
                     <span style={{ marginRight: 8 }}>✏️</span> Edit
                  </MenuItem>

                  <MenuItem
                     onClick={() => handleDelete(row.id)}
                     disabled={!hasPermissions?.canDelete}
                  >
                     <span style={{ marginRight: 8 }}>🗑️</span> Delete
                  </MenuItem>
               </Menu>
            </>
         ),
         ignoreRowClick: true,
         allowOverflow: true,
         button: true,
         width: '12%',
         grow: 0,
      },
   ];

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Technology Stack</h1>
                     </div>
                     {hasPermissions?.canAdd && (
                        <div className="btnWrap">
                           <Link to="/tech-stack/add-techstack" className="btn addBtn">
                              <PostAddIcon /> Add Tech Stack
                           </Link>
                        </div>
                     )}
                  </div>
               </div>
            </div>
            <div className="card m-3 customCard">
               {loading ? (
                  <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>
               ) : (
                  <div>
                     <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid
                           item
                           xs={12}
                           display="flex"
                           justifyContent="flex-end" // MODIFIED: aligns both search bars to the right
                           alignItems="center"
                           gap={2} // MODIFIED: adds spacing between the two search bars
                        >
                           {/* Search by Type */}
                           <div
                              style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 background: '#fff',
                                 border: '1px solid #E0E0E0',
                                 borderRadius: '12px',
                                 padding: '2px 16px',
                                 minWidth: 280,
                                 width: 280, // MODIFIED: fixed width so it matches "Search by Technology" exactly
                                 boxShadow: '0 2px 8px 0 rgba(99, 99, 99, 0.05)',
                                 height: 40,
                                 marginTop: 0,
                                 marginBottom: 0,
                              }}
                           >
                              <SearchIcon
                                 style={{
                                    color: '#B1B1B1',
                                    fontSize: 22,
                                    marginRight: 8,
                                 }}
                              />
                              <input
                                 type="text"
                                 value={searchType}
                                 onChange={handleSearchTypeChange}
                                 placeholder="Search by Type"
                                 style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: 15,
                                    color: '#232946',
                                    width: '100%',
                                    padding: 0,
                                 }}
                              />
                           </div>

                           {/* Search by Technology */}
                           <div
                              style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 background: '#fff',
                                 border: '1px solid #E0E0E0',
                                 borderRadius: '12px',
                                 padding: '2px 16px',
                                 minWidth: 280,
                                 width: 280, // MODIFIED: fixed width so both bars are the same size
                                 boxShadow: '0 2px 8px 0 rgba(99, 99, 99, 0.05)',
                                 height: 40,
                                 marginTop: 0,
                                 marginBottom: 0,
                              }}
                           >
                              <SearchIcon
                                 style={{
                                    color: '#B1B1B1',
                                    fontSize: 22,
                                    marginRight: 8,
                                 }}
                              />
                              <input
                                 type="text"
                                 value={searchTechnology}
                                 onChange={handleSearchTechnologyChange}
                                 placeholder="Search by Technology"
                                 style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: 15,
                                    color: '#232946',
                                    width: '100%',
                                    padding: 0,
                                 }}
                              />
                           </div>
                        </Grid>
                     </Grid>
                     <DataTable
                        columns={columns}
                        data={paginatedData}
                        pagination={false}
                        // Remove DataTable's built-in pagination
                        // ...existing DataTable props...
                        customStyles={{
                           rows: {
                              style: {
                                 margin: '0px',
                                 padding: 0,
                                 minHeight: '52px',
                              },
                           },
                           headCells: {
                              style: {
                                 margin: '0px',
                                 padding: '10px 15px',
                                 fontWeight: 600,
                                 fontSize: 15,
                                 background: '#3F30FF1A',
                              },
                           },
                           cells: {
                              style: {
                                 margin: '0px',
                                 padding: '10px 15px',
                                 fontSize: 15,
                                 fontWeight: 400,
                                 color: '#726E6E',
                              },
                           },
                        }}
                        className="dataTable"
                        noDataComponent={
                           <div style={{ padding: '24px', textAlign: 'center' }}>
                              No technology stacks found
                           </div>
                        }
                     />
                     {/* Custom Pagination Controls - improved design */}
                     <div
                        style={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'space-between',
                           marginTop: 24,
                           padding: '12px 0',
                           borderTop: '1px solid #eee',
                           background: '#f9f9fb',
                           borderRadius: '0 0 12px 12px',
                        }}
                     >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                           <span style={{ fontSize: 15, color: '#555' }}>Rows per page:</span>
                           <select
                              value={rowsPerPage}
                              onChange={(e) => {
                                 setRowsPerPage(Number(e.target.value));
                                 setPage(1);
                              }}
                              style={{
                                 borderRadius: 6,
                                 padding: '4px 12px',
                                 border: '1px solid #d1d5db',
                                 background: '#fff',
                                 fontSize: 15,
                                 color: '#232946',
                                 outline: 'none',
                                 minWidth: 60,
                                 cursor: 'pointer',
                              }}
                           >
                              {[5, 10, 20, 50, 100].map((opt) => (
                                 <option key={opt} value={opt}>
                                    {opt}
                                 </option>
                              ))}
                           </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                           <span style={{ fontSize: 15, color: '#555' }}>
                              {totalRows === 0
                                 ? '0-0'
                                 : `${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, totalRows)}`}
                              &nbsp;of&nbsp;{totalRows}
                           </span>
                           <button
                              onClick={() => setPage(1)}
                              disabled={page === 1}
                              style={{
                                 border: 'none',
                                 background: page === 1 ? '#e0e0e0' : '#3F30FF',
                                 color: page === 1 ? '#aaa' : '#fff',
                                 borderRadius: '50%',
                                 width: 34,
                                 height: 34,
                                 fontSize: 16,
                                 fontWeight: 700,
                                 cursor: page === 1 ? 'not-allowed' : 'pointer',
                                 transition: 'background 0.2s',
                              }}
                              title="First Page"
                           >
                              &#171;
                           </button>
                           <button
                              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                              disabled={page === 1}
                              style={{
                                 border: 'none',
                                 background: page === 1 ? '#e0e0e0' : '#3F30FF',
                                 color: page === 1 ? '#aaa' : '#fff',
                                 borderRadius: '50%',
                                 width: 34,
                                 height: 34,
                                 fontSize: 18,
                                 fontWeight: 700,
                                 cursor: page === 1 ? 'not-allowed' : 'pointer',
                                 transition: 'background 0.2s',
                              }}
                              title="Previous Page"
                           >
                              &#8249;
                           </button>
                           <span style={{ fontSize: 15, color: '#3F30FF', fontWeight: 600 }}>
                              {page}
                           </span>
                           <button
                              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                              disabled={page === totalPages || totalRows === 0}
                              style={{
                                 border: 'none',
                                 background:
                                    page === totalPages || totalRows === 0 ? '#e0e0e0' : '#3F30FF',
                                 color: page === totalPages || totalRows === 0 ? '#aaa' : '#fff',
                                 borderRadius: '50%',
                                 width: 34,
                                 height: 34,
                                 fontSize: 18,
                                 fontWeight: 700,
                                 cursor:
                                    page === totalPages || totalRows === 0
                                       ? 'not-allowed'
                                       : 'pointer',
                                 transition: 'background 0.2s',
                              }}
                              title="Next Page"
                           >
                              &#8250;
                           </button>
                           <button
                              onClick={() => setPage(totalPages)}
                              disabled={page === totalPages || totalRows === 0}
                              style={{
                                 border: 'none',
                                 background:
                                    page === totalPages || totalRows === 0 ? '#e0e0e0' : '#3F30FF',
                                 color: page === totalPages || totalRows === 0 ? '#aaa' : '#fff',
                                 borderRadius: '50%',
                                 width: 34,
                                 height: 34,
                                 fontSize: 16,
                                 fontWeight: 700,
                                 cursor:
                                    page === totalPages || totalRows === 0
                                       ? 'not-allowed'
                                       : 'pointer',
                                 transition: 'background 0.2s',
                              }}
                              title="Last Page"
                           >
                              &#187;
                           </button>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default TechStackList;
